import React, { useState } from "react";
import { Alert, Button, Modal } from "react-bootstrap";
import { deleteRequest } from "../../services/api";
import { Contacts } from "../../sitel/demon/ui/atoms";

/**
 * A React component that renders a delete button triggering a confirmation modal for deleting an OH request.
 *
 * @param {object} props - The component props.
 * @param {string} props.requestId - The ID of the OH request to be deleted.
 * @returns {JSX.Element} - The DeleteRequestButtonWithConfirmationModal component.
 */
export default function DeleteRequestButtonWithConfirmationModal({ requestId }) {
  const [showDeleteRequestModal, setShowDeleteRequestModal] = useState(false);
  const [showErrorRequestModal, setShowErrorRequestModal] = useState(false);
  const [errorCount, setErrorCount] = useState(0);

  /**
   * Handles the closing of the error request modal.
   */
  const handleCloseErrorRequestModal = () => {
    setShowErrorRequestModal(false);
    setErrorCount(1);
  };

  return (
    <>
      <Button
        variant="danger"
        size="sm"
        className="py-0"
        onClick={() => setShowDeleteRequestModal(true)}
      >
        <i className="fa-regular fa-trash-can fw-bold"></i>
      </Button>
      <DeleteRequestModal
        showDeleteRequestModal={showDeleteRequestModal}
        setShowDeleteRequestModal={setShowDeleteRequestModal}
        selectedRequestId={requestId}
        deleteRequest={deleteRequest}
        setShowErrorRequestModal={setShowErrorRequestModal}
      />
      <ErrorRequestModal
        showErrorRequestModal={showErrorRequestModal}
        handleCloseErrorRequestModal={handleCloseErrorRequestModal}
        errorCount={errorCount}
      />
    </>
  );
}

/**
 * A modal component for confirming the deletion of an OH request.
 *
 * @param {object} props - The component props.
 * @param {boolean} props.showDeleteRequestModal - Controls the visibility of the modal.
 * @param {function} props.setShowDeleteRequestModal - Function to update the modal visibility.
 * @param {string} props.selectedRequestId - The ID of the OH request to be deleted.
 * @param {function} props.deleteRequest - Function to delete the OH request.
 * @param {function} props.setShowErrorRequestModal - Function to update error modal visibility.
 * @returns {JSX.Element} - The DeleteRequestModal component.
 */
const DeleteRequestModal = ({
  showDeleteRequestModal,
  setShowDeleteRequestModal,
  selectedRequestId,
  deleteRequest,
  setShowErrorRequestModal,
}) => {
  /**
   * Handles the confirmation of OH request deletion.
   */
  const handleConfirmDelete = () => {
    deleteRequest(
      selectedRequestId,
      (response) => {
        console.log('handleConfirmDelete', response);
        window.location.href = "/oh-requests";
      },
      (error) => {
        console.error('error has occurred trying to delete request', error);
        setShowErrorRequestModal(true);
      }
    );
    setShowDeleteRequestModal(false);
  };

  return (
    <Modal show={showDeleteRequestModal} onHide={() => setShowDeleteRequestModal(false)}>
      <Modal.Body className="p-4 text-center">
        <h5 className="fw-bold">Are you sure you want to DELETE this request?</h5>
        <p className="mb-0">This action cannot be reversed.</p>
      </Modal.Body>
      <Modal.Footer className="flex-nowrap p-0">
        <Button
          variant="link"
          className="fs-6 text-decoration-none col-6 py-3 m-0 rounded-0 border-end"
          onClick={handleConfirmDelete}
        >
          <strong>Yes</strong>
        </Button>
        <Button
          variant="link"
          className="fs-6 text-decoration-none col-6 py-3 m-0 rounded-0"
          onClick={() => setShowDeleteRequestModal(false)}
        >
          No
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * A modal component for displaying an error when deleting an OH request.
 *
 * @param {object} props - The component props.
 * @param {boolean} props.showErrorRequestModal - Controls the visibility of the error modal.
 * @param {function} props.handleCloseErrorRequestModal - Function to close the error modal.
 * @param {number} props.errorCount - The count of errors occurred during deletion.
 * @returns {JSX.Element} - The ErrorRequestModal component.
 */
const ErrorRequestModal = ({ showErrorRequestModal, handleCloseErrorRequestModal, errorCount }) => {
  return (
    <Modal show={showErrorRequestModal} onHide={handleCloseErrorRequestModal}>
      <Modal.Body className="p-3 text-center">
        <h5 className="fw-bold mb-4">An error occurred trying to delete this request</h5>
        {errorCount === 0 ? (
          <Alert variant="warning">Please try again</Alert>
        ) : (
          <Alert variant="danger">
            <Contacts.SitelHelpDeskLanguage />
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className="flex-nowrap p-0">
        <Button
          variant="link"
          className="fs-6 text-decoration-none col-12 py-3 m-0 rounded-0 border-end"
          onClick={handleCloseErrorRequestModal}
        >
          <strong>Ok</strong>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
